import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthSafe } from '@/contexts/AuthContext';
import { shouldShowPublicWidgets } from '@/lib/integrations/hubWidgets';

/**
 * Mounts the iii Agent Hub unified tracking loader (track.js) on public,
 * logged-out marketing pages only.
 *
 * We adopt the unified loader with `data-skip="posthog,voice"`: SettleWise
 * keeps its own PostHog init (src/lib/posthog.ts) and its custom pre-call
 * voice gate (index.html), and lets the loader manage the pixel + the "Sam"
 * support chat uniformly with every other brand.
 *
 * Scope is public-only because the authenticated app already ships its own
 * support bubble (Kodey); see {@link shouldShowPublicWidgets}. Because we never
 * load it while authenticated, `data-authenticated` is unnecessary.
 *
 * Rendered once near the root (inside Providers, so auth context is available).
 * Injects the loader when the route qualifies and removes it (plus any UI it
 * injected) when navigating into the app or after sign-in.
 */
const TRACK_SCRIPT_ID = 'iii-agent-hub-track';
const TRACK_SRC = 'https://iii-agent-hub.scott-4bd.workers.dev/track.js';

export default function HubPublicWidgets() {
  const location = useLocation();
  const { user, loading } = useAuthSafe();

  const show = shouldShowPublicWidgets({
    pathname: location.pathname,
    isAuthenticated: !!user,
    loading,
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const existing = document.getElementById(TRACK_SCRIPT_ID);

    if (show) {
      if (!existing) {
        const s = document.createElement('script');
        s.id = TRACK_SCRIPT_ID;
        s.src = TRACK_SRC;
        s.async = true;
        s.dataset.brand = 'settlewise';
        // Keep our own PostHog + custom voice gate; let the loader own pixel + chat.
        s.dataset.skip = 'posthog,voice';
        document.body.appendChild(s);
      }
    } else if (existing) {
      existing.remove();
      // Best-effort: remove any UI the loader injected so it does not linger
      // after an SPA navigation into the authenticated app.
      document
        .querySelectorAll('[id^="iii-agent-hub"],[class*="iii-agent-hub"]')
        .forEach((node) => node.remove());
    }
  }, [show]);

  return null;
}
